/**
 * Parses a captured RECODE block back into a RecodeForm (for round-trip
 * editing). Mirrors the backend's FindTopLevelAssignmentEquals: the assignment
 * `=` is the first one at depth 0 that is not part of `== >= <= !=`, ignoring
 * nesting inside `()`, `{}`, and `<<>>`.
 *
 * Best-effort: malformed input still produces a partial form plus an errors
 * array, which the wizard surfaces in a warning Alert (like the Logic Wizard).
 */
import { ASTNode, unquoteLabel } from "../logicWizard/grammar"
import { parseIfLine } from "../logicWizard/ifParser"
import { parseArith } from "./arithParser"
import {
  makeEmptyRecodeForm,
  RecodeForm,
  SelectNConfig,
} from "./recodeModel"

const PLACEHOLDER: ASTNode = { kind: "placeholder" }

/** Index of the assignment `=`, or -1 if none at top level. */
export function findTopLevelEquals(line: string): number {
  let paren = 0
  let brace = 0
  let angle = 0
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (c === "<" && line[i + 1] === "<") {
      angle++
      i++
      continue
    }
    if (c === ">" && line[i + 1] === ">") {
      if (angle > 0) angle--
      i++
      continue
    }
    if (c === "(") paren++
    else if (c === ")") {
      if (paren > 0) paren--
    } else if (c === "{") brace++
    else if (c === "}") {
      if (brace > 0) brace--
    } else if (c === "=" && paren === 0 && brace === 0 && angle === 0) {
      const prev = line[i - 1]
      const next = line[i + 1]
      // Skip `==`, `>=`, `<=`, `!=`.
      if (prev === "=" || prev === ">" || prev === "<" || prev === "!") continue
      if (next === "=") continue
      return i
    }
  }
  return -1
}

/** Index of a top-level `::`, ignoring `<<>>`; -1 if none. */
function findTopLevelDoubleColon(s: string): number {
  let angle = 0
  for (let i = 0; i < s.length - 1; i++) {
    const c = s[i]
    if (c === "<" && s[i + 1] === "<") {
      angle++
      i++
      continue
    }
    if (c === ">" && s[i + 1] === ">") {
      if (angle > 0) angle--
      i++
      continue
    }
    if (c === ":" && s[i + 1] === ":" && angle === 0) return i
  }
  return -1
}

/** Split on commas that sit at depth 0 (outside `()`, `{}`, `<<>>`). */
function splitTopLevelCommas(s: string): string[] {
  const parts: string[] = []
  let paren = 0
  let brace = 0
  let angle = 0
  let start = 0
  for (let i = 0; i < s.length; i++) {
    const c = s[i]
    if (c === "<" && s[i + 1] === "<") {
      angle++
      i++
      continue
    }
    if (c === ">" && s[i + 1] === ">") {
      if (angle > 0) angle--
      i++
      continue
    }
    if (c === "(") paren++
    else if (c === ")") {
      if (paren > 0) paren--
    } else if (c === "{") brace++
    else if (c === "}") {
      if (brace > 0) brace--
    } else if (c === "," && paren === 0 && brace === 0 && angle === 0) {
      parts.push(s.slice(start, i))
      start = i + 1
    }
  }
  parts.push(s.slice(start))
  return parts
}

/** Content between the `(` at `openIdx` and its matching `)`, or null. */
function extractBalancedParen(s: string, openIdx: number): string | null {
  let depth = 0
  for (let i = openIdx; i < s.length; i++) {
    if (s[i] === "(") depth++
    else if (s[i] === ")") {
      depth--
      if (depth === 0) return s.slice(openIdx + 1, i)
    }
  }
  return null
}

interface ParsedAssignment {
  target: string
  option?: string
  rhs: string
  ast?: ASTNode
}

function parseAssignment(line: string, errors: string[]): ParsedAssignment | null {
  const eq = findTopLevelEquals(line)
  if (eq < 0) {
    errors.push(`Assignment is missing '=' : ${line}`)
    return null
  }
  const lhs = line.slice(0, eq).trim()
  const rhs = line.slice(eq + 1).trim()
  const colon = findTopLevelDoubleColon(lhs)
  if (colon < 0) {
    return { target: unquoteLabel(lhs), rhs }
  }
  const target = unquoteLabel(lhs.slice(0, colon).trim())
  const option = unquoteLabel(lhs.slice(colon + 2).trim())
  const { ast, errors: e } = parseIfLine(rhs)
  errors.push(...e)
  return { target, option, rhs, ast }
}

function tryParseSelectN(
  rhs: string,
  errors: string[],
): SelectNConfig | null {
  if (!/^SELECT_N\s*\(/i.test(rhs)) return null
  const open = rhs.indexOf("(")
  const inner = extractBalancedParen(rhs, open)
  if (inner === null) {
    errors.push("SELECT_N: unbalanced parentheses")
    return null
  }
  const args = splitTopLevelCommas(inner).map((a) => a.trim())
  if (args.length < 2) {
    errors.push("SELECT_N needs a source and a count")
  }
  const source = unquoteLabel(args[0] ?? "")
  const n = Number(args[1])
  if (!Number.isInteger(n) || n < 1) {
    errors.push("SELECT_N count must be a whole number")
  }
  let priority: string[] = []
  if (args[2] !== undefined) {
    const set = args[2]
    if (!set.startsWith("{") || !set.endsWith("}")) {
      errors.push("SELECT_N priority must be a { } list")
    } else {
      const body = set.slice(1, -1).trim()
      priority = body
        ? splitTopLevelCommas(body).map((s) => unquoteLabel(s.trim()))
        : []
    }
  }
  return { source, n: Number.isInteger(n) ? n : 1, priority }
}

export interface RecodeParseResult {
  form: RecodeForm
  errors: string[]
}

export function parseRecodeBlock(text: string): RecodeParseResult {
  const errors: string[] = []
  const form = makeEmptyRecodeForm()

  const lines = text.split("\n")
  const recodeIdx = lines.findIndex((l) => /^\s*RECODE\b/i.test(l))
  if (recodeIdx < 0) {
    errors.push("No RECODE keyword found")
    return { form, errors }
  }

  const firstRemainder = lines[recodeIdx].replace(/^\s*RECODE\b\s*/i, "").trim()
  const assignmentLines: string[] = []
  if (firstRemainder && findTopLevelEquals(firstRemainder) >= 0) {
    // Single-line form: `RECODE <assignment>`.
    assignmentLines.push(firstRemainder)
  } else {
    // Block form: following assignment lines until a blank/comment/non-assignment.
    for (let i = recodeIdx + 1; i < lines.length; i++) {
      const t = lines[i].trim()
      if (!t || t.startsWith("#")) break
      if (findTopLevelEquals(t) < 0) break
      assignmentLines.push(t)
    }
  }

  if (assignmentLines.length === 0) {
    // Bare `RECODE` — nothing to populate; wizard opens in create mode.
    return { form, errors }
  }

  const parsed = assignmentLines
    .map((l) => parseAssignment(l, errors))
    .filter((p): p is ParsedAssignment => p !== null)
  if (parsed.length === 0) return { form, errors }

  form.target = parsed[0].target
  const hasOption = parsed.some((p) => p.option !== undefined)

  if (hasOption) {
    form.mode = "bucket"
    form.buckets = parsed
      .filter((p) => {
        if (p.target !== form.target) {
          errors.push(
            `Ignoring assignment for a different target "${p.target}". The wizard edits one target at a time.`,
          )
          return false
        }
        return true
      })
      .map((p) => ({ option: p.option ?? "", ast: p.ast ?? PLACEHOLDER }))
    return { form, errors }
  }

  // Whole-target: SELECT_N or arithmetic.
  const rhs = parsed[0].rhs
  const selectN = tryParseSelectN(rhs, errors)
  if (selectN) {
    form.mode = "select_n"
    form.selectN = selectN
  } else {
    form.mode = "arithmetic"
    form.arithmeticText = rhs
    errors.push(...parseArith(rhs).errors)
  }
  return { form, errors }
}
